import * as React from 'react';
import { cn } from '../../lib/utils';

interface SliderProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number[];
  onValueChange: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      className,
      value,
      onValueChange,
      min = 0,
      max = 100,
      step = 1,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const [localValue, setLocalValue] = React.useState(value);

    React.useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (
      index: number,
      newValue: number,
      shouldNotify: boolean = true
    ) => {
      // Redondear para evitar decimales largos
      const roundedValue = Math.round(newValue * 10) / 10;
      const newValues = [...localValue];
      newValues[index] = Math.min(Math.max(roundedValue, min), max);

      // Para range slider, asegurar que min <= max
      if (newValues.length === 2) {
        if (index === 0 && newValues[0] > newValues[1]) {
          newValues[1] = newValues[0];
        } else if (index === 1 && newValues[1] < newValues[0]) {
          newValues[0] = newValues[1];
        }
      }

      setLocalValue(newValues);

      // Solo notificar cambios cuando se especifique (al final del drag)
      if (shouldNotify) {
        onValueChange(newValues);
      }
    };

    const getPercentage = (val: number) => ((val - min) / (max - min)) * 100;

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex items-center w-full h-6',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        {...props}
      >
        {/* Track */}
        <div className='relative flex-1 h-2 bg-gray-200 rounded-full'>
          {/* Range highlight for dual sliders */}
          {localValue.length === 2 && (
            <div
              className='absolute h-full bg-blue-600 rounded-full'
              style={{
                left: `${getPercentage(localValue[0])}%`,
                width: `${getPercentage(localValue[1]) - getPercentage(localValue[0])}%`,
              }}
            />
          )}

          {/* Thumbs */}
          {localValue.map((val, index) => (
            <div
              key={index}
              className='absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-white border-2 border-blue-600 rounded-full shadow-sm cursor-pointer hover:bg-blue-50 transition-colors'
              style={{ left: `${getPercentage(val)}%` }}
              onMouseDown={e => {
                if (disabled) return;

                const startX = e.clientX;
                const startValue = val;
                const containerRect = e.currentTarget
                  .closest('.relative')
                  ?.getBoundingClientRect();

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  if (!containerRect) return;

                  const deltaX = moveEvent.clientX - startX;
                  const deltaPercent = (deltaX / containerRect.width) * 100;
                  const deltaValue = (deltaPercent / 100) * (max - min);
                  const newValue =
                    Math.round((startValue + deltaValue) / step) * step;

                  // Solo actualizar visualmente, sin notificar cambios
                  handleChange(index, newValue, false);
                };

                const handleMouseUp = () => {
                  // Al soltar, notificar el cambio final
                  const currentValue = localValue[index];
                  handleChange(index, currentValue, true);

                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              role='slider'
              aria-valuemin={min}
              aria-valuemax={max}
              aria-valuenow={val}
              tabIndex={disabled ? -1 : 0}
            >
              {/* Value label */}
              <div className='absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap'>
                {val % 1 === 0 ? val.toString() : val.toFixed(1)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

Slider.displayName = 'Slider';

export { Slider };
